import { type ComponentProps, type ReactNode, useId } from 'react';
import type { ImageType } from './types';

type IllustrationProps = ComponentProps<'svg'>;

/** A prefix for the ids inside one picture, so two pictures on a page never share a mask or filter. */
function useSvgId() {
  return useId().replace(/[^\w-]/g, '');
}

/** A white card on the grey disc, cut off by the disc's lower edge, with an optional badge at the top right. */
function CardIllustration({
  badge,
  children,
  ...props
}: IllustrationProps & { badge?: ReactNode }) {
  const id = useSvgId();
  return (
    <svg width="160" height="160" fill="none" viewBox="0 0 160 160" aria-hidden="true" {...props}>
      <path
        fill="#f0f1f2"
        d="M154 86c0 40.87-33.13 74-74 74S6 126.87 6 86s33.13-74 74-74 74 33.13 74 74"
      />
      <mask
        id={`${id}-b`}
        width="148"
        height="160"
        x="6"
        y="0"
        maskUnits="userSpaceOnUse"
        style={{ maskType: 'luminance' }}
      >
        <path fill="#fff" d="M154 86c0 40.87-33.13 74-74 74S6 126.87 6 86V0h148z" />
      </mask>
      <g mask={`url(#${id}-b)`}>
        <g filter={`url(#${id}-a)`}>
          <rect width="96" height="150" x="32" y="16" fill="#fff" rx="6" />
        </g>
        <rect width="40" height="6" x="40" y="26" rx="3" className="fill-illustration-primary" />
        {children}
      </g>
      {badge && (
        <>
          <circle cx="124" cy="22" r="12" className="fill-illustration-secondary" />
          {badge}
        </>
      )}
      <defs>
        <filter
          id={`${id}-a`}
          width="116"
          height="170"
          x="22"
          y="8"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

function DataTablesIllustration(props: IllustrationProps) {
  return (
    <CardIllustration
      {...props}
      badge={<path stroke="#fff" strokeLinecap="round" strokeWidth="2" d="M118 22h12m-6-6v12" />}
    >
      <rect width="80" height="12" x="40" y="40" fill="#f0f1f2" rx="2" />
      <rect width="14" height="3" x="44" y="44.5" fill="#b6babf" rx="1.5" />
      <rect width="14" height="3" x="70" y="44.5" fill="#b6babf" rx="1.5" />
      <rect width="14" height="3" x="96" y="44.5" fill="#b6babf" rx="1.5" />
      <path stroke="#e8e9eb" d="M66 52v60m26-60v60M40 66h80M40 80h80M40 94h80M40 108h80" />
      <rect
        width="25"
        height="13"
        x="66.75"
        y="66.75"
        fill="#fff"
        strokeWidth="1.5"
        rx="1.25"
        className="stroke-illustration-primary"
      />
      <rect width="16" height="3" x="44" y="60.5" fill="#d2d5d9" rx="1.5" />
      <rect width="12" height="3" x="70" y="60.5" fill="#d2d5d9" rx="1.5" />
      <rect width="18" height="3" x="96" y="60.5" fill="#d2d5d9" rx="1.5" />
      <rect width="12" height="3" x="44" y="74.5" fill="#d2d5d9" rx="1.5" />
      <rect width="18" height="3" x="70" y="74.5" rx="1.5" className="fill-illustration-primary" />
      <rect width="10" height="3" x="96" y="74.5" fill="#d2d5d9" rx="1.5" />
      <rect width="18" height="3" x="44" y="88.5" fill="#d2d5d9" rx="1.5" />
      <rect width="10" height="3" x="70" y="88.5" fill="#d2d5d9" rx="1.5" />
      <rect width="14" height="3" x="96" y="88.5" fill="#d2d5d9" rx="1.5" />
      <rect width="14" height="3" x="44" y="102.5" fill="#d2d5d9" rx="1.5" />
      <rect width="16" height="3" x="70" y="102.5" fill="#d2d5d9" rx="1.5" />
      <rect width="12" height="3" x="96" y="102.5" fill="#d2d5d9" rx="1.5" />
      <rect
        width="35"
        height="11"
        x="40.5"
        y="118.5"
        fill="#fff"
        stroke="#d2d5d9"
        strokeDasharray="3 2"
        rx="2.5"
      />
      <path stroke="#b6babf" strokeLinecap="round" strokeWidth="1.5" d="M46 124h5m-2.5-2.5v5" />
      <rect width="16" height="3" x="54" y="122.5" fill="#d2d5d9" rx="1.5" />
    </CardIllustration>
  );
}

function FormsIllustration(props: IllustrationProps) {
  return (
    <CardIllustration
      {...props}
      badge={
        <path
          stroke="#fff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="m118.5 22 3.5 3.5 7-7"
        />
      }
    >
      <rect width="64" height="4" x="40" y="37" fill="#d2d5d9" rx="2" />
      <rect width="26" height="4" x="40" y="51" fill="#b6babf" rx="2" />
      <rect
        width="79"
        height="11"
        x="40.75"
        y="58.75"
        fill="#fff"
        stroke="#d2d5d9"
        strokeWidth="1.5"
        rx="2.25"
      />
      <rect width="30" height="3" x="46" y="62.5" fill="#e8e9eb" rx="1.5" />
      <rect width="34" height="4" x="40" y="78" fill="#b6babf" rx="2" />
      <rect
        width="79"
        height="11"
        x="40.75"
        y="85.75"
        fill="#fff"
        strokeWidth="1.5"
        rx="2.25"
        className="stroke-illustration-primary"
      />
      <rect width="18" height="3" x="46" y="89.5" fill="#d2d5d9" rx="1.5" />
      <rect
        width="1.5"
        height="7"
        x="65.5"
        y="87.5"
        rx=".75"
        className="fill-illustration-primary"
      />
      <rect width="10" height="10" x="40" y="105" rx="2" className="fill-illustration-primary" />
      <path
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="m42.5 110 2 2 3.5-4"
      />
      <rect width="42" height="4" x="55" y="108" fill="#d2d5d9" rx="2" />
      <rect width="36" height="12" x="40" y="123" rx="3" className="fill-illustration-primary" />
      <rect width="20" height="3" x="48" y="127.5" fill="#fff" rx="1.5" />
    </CardIllustration>
  );
}

/** A trigger leading to a condition that branches into two actions. */
function WorkflowsIllustration(props: IllustrationProps) {
  return (
    <CardIllustration
      {...props}
      badge={<path fill="#fff" d="m125.5 13-7 10.5h5.5l-1.5 7.5 7-10.5H124z" />}
    >
      <rect
        width="55"
        height="15"
        x="52.75"
        y="40.75"
        fill="#fff"
        strokeWidth="1.5"
        rx="3.25"
        className="stroke-illustration-primary"
      />
      <rect width="8" height="8" x="57" y="44.5" rx="2" className="fill-illustration-primary" />
      <rect width="30" height="3" x="69" y="47" fill="#d2d5d9" rx="1.5" />
      <path
        stroke="#b6babf"
        strokeLinecap="round"
        strokeWidth="1.5"
        d="M80 56.5v8M72 74H56v12M88 74h16v12M56 102v10"
      />
      <path fill="#fff" stroke="#b6babf" strokeWidth="1.5" d="m80 66 8 8-8 8-8-8z" />
      <rect
        width="31"
        height="15"
        x="40.75"
        y="86.75"
        fill="#fff"
        stroke="#d2d5d9"
        strokeWidth="1.5"
        rx="3.25"
      />
      <rect width="7" height="7" x="45" y="90.5" rx="1.5" className="fill-illustration-secondary" />
      <rect width="13" height="3" x="55" y="92.5" fill="#d2d5d9" rx="1.5" />
      <rect
        width="31"
        height="15"
        x="88.75"
        y="86.75"
        fill="#fff"
        stroke="#d2d5d9"
        strokeWidth="1.5"
        rx="3.25"
      />
      <rect width="7" height="7" x="93" y="90.5" rx="1.5" className="fill-illustration-secondary" />
      <rect width="13" height="3" x="103" y="92.5" fill="#d2d5d9" rx="1.5" />
      <rect
        width="31"
        height="13"
        x="40.5"
        y="112.5"
        fill="#fff"
        stroke="#d2d5d9"
        strokeDasharray="3 2"
        rx="3"
      />
      <path stroke="#b6babf" strokeLinecap="round" strokeWidth="1.5" d="M53 119h6m-3-3v6" />
    </CardIllustration>
  );
}

/** View tabs over an entries table that has nothing to show until a view is picked. */
function FormViewsIllustration(props: IllustrationProps) {
  return (
    <CardIllustration
      {...props}
      badge={
        <>
          <path
            stroke="#fff"
            strokeLinejoin="round"
            strokeWidth="1.75"
            d="M116.5 22s2.75-4.5 7.5-4.5 7.5 4.5 7.5 4.5-2.75 4.5-7.5 4.5-7.5-4.5-7.5-4.5Z"
          />
          <circle cx="124" cy="22" r="2" fill="#fff" />
        </>
      }
    >
      <rect width="18" height="8" x="40" y="40" fill="#f0f1f2" rx="4" />
      <rect width="18" height="8" x="61" y="40" fill="#f0f1f2" rx="4" />
      <rect width="18" height="8" x="82" y="40" fill="#f0f1f2" rx="4" />
      <rect
        width="16"
        height="7"
        x="103.5"
        y="40.5"
        fill="#fff"
        stroke="#d2d5d9"
        strokeDasharray="2 1.5"
        rx="3.5"
      />
      <path stroke="#b6babf" strokeLinecap="round" d="M109.5 44h4m-2-2v4" />
      <rect width="80" height="10" x="40" y="56" fill="#f0f1f2" rx="2" />
      <rect width="14" height="3" x="44" y="59.5" fill="#d2d5d9" rx="1.5" />
      <rect width="14" height="3" x="70" y="59.5" fill="#d2d5d9" rx="1.5" />
      <rect width="14" height="3" x="96" y="59.5" fill="#d2d5d9" rx="1.5" />
      <path stroke="#f0f1f2" d="M40 78h80M40 90h80M40 102h80M40 114h80" />
      <rect width="18" height="3" x="44" y="71" fill="#f0f1f2" rx="1.5" />
      <rect width="18" height="3" x="44" y="83" fill="#f0f1f2" rx="1.5" />
      <rect width="18" height="3" x="44" y="95" fill="#f0f1f2" rx="1.5" />
      <rect width="18" height="3" x="44" y="107" fill="#f0f1f2" rx="1.5" />
      <path
        className="fill-illustration-primary"
        d="M64 39.5v12.2c0 .5.6.7.9.4l2.8-2.8 2 4.6c.1.3.5.4.8.3l1.4-.6c.3-.1.4-.5.3-.8l-2-4.5h4c.5 0 .7-.6.4-.9l-9.7-8.6c-.3-.3-.9 0-.9.4"
      />
    </CardIllustration>
  );
}

/** A form whose fields are marked internal, kept away from the customer. */
function InternalFieldsIllustration(props: IllustrationProps) {
  return (
    <CardIllustration
      {...props}
      badge={
        <>
          <rect width="11" height="8.5" x="118.5" y="20.5" fill="#fff" rx="1.5" />
          <path stroke="#fff" strokeWidth="1.75" d="M121 20.5v-2a3 3 0 0 1 6 0v2" />
        </>
      }
    >
      <rect width="64" height="4" x="40" y="37" fill="#d2d5d9" rx="2" />
      <rect width="26" height="4" x="40" y="51" fill="#b6babf" rx="2" />
      <rect
        width="17"
        height="7"
        x="69"
        y="49.5"
        rx="3.5"
        className="fill-illustration-secondary"
      />
      <rect width="9" height="2" x="73" y="52" fill="#fff" rx="1" />
      <rect
        width="79"
        height="11"
        x="40.75"
        y="58.75"
        fill="#fff"
        stroke="#d2d5d9"
        strokeWidth="1.5"
        rx="2.25"
      />
      <rect width="30" height="3" x="46" y="62.5" fill="#e8e9eb" rx="1.5" />
      <rect width="34" height="4" x="40" y="78" fill="#b6babf" rx="2" />
      <rect
        width="17"
        height="7"
        x="77"
        y="76.5"
        rx="3.5"
        className="fill-illustration-secondary"
      />
      <rect width="9" height="2" x="81" y="79" fill="#fff" rx="1" />
      <rect
        width="79"
        height="11"
        x="40.75"
        y="85.75"
        fill="#fff"
        stroke="#d2d5d9"
        strokeWidth="1.5"
        rx="2.25"
      />
      <rect width="22" height="3" x="46" y="89.5" fill="#e8e9eb" rx="1.5" />
      <rect
        width="79"
        height="23"
        x="40.5"
        y="106.5"
        fill="#fff"
        stroke="#d2d5d9"
        strokeDasharray="3 2"
        rx="3"
      />
      <path stroke="#b6babf" strokeLinecap="round" strokeWidth="1.5" d="M80 115v6m-3-3h6" />
    </CardIllustration>
  );
}

/** A checklist with every item ticked off. */
function AllDoneIllustration(props: IllustrationProps) {
  const rows = [42, 60, 78, 96, 114];
  return (
    <CardIllustration
      {...props}
      badge={
        <path
          fill="#fff"
          d="m124 14.5 2.2 4.6 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5-3.6-3.5 5-.7z"
        />
      }
    >
      {rows.map((y, index) => (
        <g key={y}>
          <circle cx="46" cy={y + 5} r="5" className="fill-illustration-primary" />
          <path
            stroke="#fff"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d={`m43.5 ${y + 5} 1.75 1.75 3.25-3.5`}
          />
          <rect
            width={[44, 36, 50, 30, 40][index]}
            height="4"
            x="56"
            y={y + 1}
            fill="#d2d5d9"
            rx="2"
          />
          <rect
            width={[30, 40, 24, 36, 28][index]}
            height="3"
            x="56"
            y={y + 7}
            fill="#e8e9eb"
            rx="1.5"
          />
        </g>
      ))}
    </CardIllustration>
  );
}

/** A plug that doesn't fit the socket next to it. */
function UnsupportedIllustration(props: IllustrationProps) {
  return (
    <CardIllustration
      {...props}
      badge={
        <path stroke="#fff" strokeLinecap="round" strokeWidth="2" d="m119.5 17.5 9 9m0-9-9 9" />
      }
    >
      <rect width="64" height="4" x="40" y="37" fill="#d2d5d9" rx="2" />
      <rect width="24" height="22" x="42" y="58" fill="#d2d5d9" rx="4" />
      <rect width="9" height="3.5" x="66" y="62.5" fill="#b6babf" rx="1" />
      <rect width="9" height="3.5" x="66" y="71.5" fill="#b6babf" rx="1" />
      <path stroke="#d2d5d9" strokeLinecap="round" strokeWidth="3" d="M42 69h-4" />
      <rect
        width="23"
        height="21"
        x="94.5"
        y="58.5"
        fill="#fff"
        strokeDasharray="3 2"
        strokeWidth="1.5"
        rx="3.5"
        className="stroke-illustration-primary"
      />
      <rect width="7" height="3" x="98" y="64" rx="1" className="fill-illustration-primary" />
      <rect width="7" height="3" x="98" y="73" rx="1" className="fill-illustration-primary" />
      <rect width="72" height="4" x="44" y="98" fill="#d2d5d9" rx="2" />
      <rect width="52" height="3" x="44" y="107" fill="#e8e9eb" rx="1.5" />
      <rect width="60" height="3" x="44" y="115" fill="#e8e9eb" rx="1.5" />
    </CardIllustration>
  );
}

/** A search box over a grid of products. */
function ProductSearchIllustration(props: IllustrationProps) {
  return (
    <CardIllustration
      {...props}
      badge={
        <path
          stroke="#fff"
          strokeLinecap="round"
          strokeWidth="2"
          d="m126 24 3.5 3.5M127 20a5 5 0 1 1-10 0 5 5 0 0 1 10 0"
        />
      }
    >
      <rect
        width="79"
        height="11"
        x="40.75"
        y="38.75"
        fill="#fff"
        strokeWidth="1.5"
        rx="5.5"
        className="stroke-illustration-primary"
      />
      <path
        strokeLinecap="round"
        strokeWidth="1.25"
        d="m50.5 46.5-1.75-1.75M49.5 43a2.75 2.75 0 1 1-5.5 0 2.75 2.75 0 0 1 5.5 0"
        className="stroke-illustration-primary"
      />
      <rect width="24" height="3" x="54" y="42.75" fill="#d2d5d9" rx="1.5" />
      {[
        [40, 58],
        [82, 58],
        [40, 98],
        [82, 98],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <rect width="38" height="34" x={x} y={y} fill="#f0f1f2" rx="3" />
          <rect width="16" height="14" x={(x ?? 0) + 11} y={(y ?? 0) + 4} fill="#d2d5d9" rx="2" />
          <rect width="24" height="3" x={(x ?? 0) + 4} y={(y ?? 0) + 22} fill="#d2d5d9" rx="1.5" />
          <rect width="12" height="3" x={(x ?? 0) + 4} y={(y ?? 0) + 27} fill="#b6babf" rx="1.5" />
        </g>
      ))}
    </CardIllustration>
  );
}

function CustomerGroupIllustration(props: IllustrationProps) {
  const id = useSvgId();
  return (
    <svg width="148" height="154" fill="none" viewBox="0 0 148 154" aria-hidden="true" {...props}>
      <path
        fill="#f0f1f2"
        d="M148 80c0 40.87-33.13 74-74 74S0 120.87 0 80 33.13 6 74 6s74 33.13 74 74"
      />
      <mask
        id={`${id}-b`}
        width="148"
        height="154"
        x="0"
        y="0"
        maskUnits="userSpaceOnUse"
        style={{ maskType: 'luminance' }}
      >
        <path fill="#fff" d="M148 80c0 40.87-33.13 74-74 74S0 120.87 0 80V0h148z" />
      </mask>
      <g filter={`url(#${id}-a)`} mask={`url(#${id}-b)`}>
        <path
          fill="#fff"
          d="M19.77 154h107.47c2.083 0 3.77-2.098 3.761-4.688V11a4 4 0 0 0-4-4H20a4 4 0 0 0-4 4v138.312c0 2.59 1.687 4.688 3.77 4.688"
        />
      </g>
      <path
        fill="#d2d5d9"
        fillRule="evenodd"
        d="M109.223 101.277a.94.94 0 0 0-.667-.277h-6.612a.944.944 0 0 0-.944.944v6.612c0 .25.1.49.277.667l8.5 8.5a.943.943 0 0 0 1.335 0l6.611-6.611a.943.943 0 0 0 0-1.335zm-4.917 4.445a1.416 1.416 0 1 1-.001-2.833 1.416 1.416 0 0 1 .001 2.833"
        clipRule="evenodd"
      />
      <path
        fill="#d2d5d9"
        d="M59 107.5c0-1.381 1.02-2.5 2.28-2.5h16.44c1.259 0 2.28 1.119 2.28 2.5s-1.021 2.5-2.28 2.5H61.28c-1.26 0-2.28-1.119-2.28-2.5m0 9c0-1.381 1.04-2.5 2.323-2.5h25.354c1.283 0 2.323 1.119 2.323 2.5s-1.04 2.5-2.323 2.5H61.323C60.04 119 59 117.881 59 116.5m-16.096-10.812c0 2.589-1.972 4.688-4.404 4.688s-4.404-2.099-4.404-4.688S36.068 101 38.5 101s4.404 2.099 4.404 4.688m-12.205 10.278c1.446-1.211 4.323-3.17 7.801-3.17s6.355 1.959 7.801 3.17c.645.54.847 1.453.592 2.284l-.348 1.134c-.296.964-1.14 1.616-2.093 1.616H32.548c-.953 0-1.798-.652-2.093-1.616l-.348-1.134c-.255-.831-.053-1.744.592-2.284"
      />
      <path
        fill="#f0f1f2"
        d="M121 15H28a4 4 0 0 0-4 4v32a4 4 0 0 0 4 4h93a4 4 0 0 0 4-4V19a4 4 0 0 0-4-4"
      />
      <path
        d="M42.904 29.688c0 2.59-1.972 4.688-4.404 4.688s-4.404-2.099-4.404-4.688c0-2.59 1.972-4.688 4.404-4.688s4.404 2.099 4.404 4.688M30.699 39.966c1.446-1.21 4.323-3.17 7.801-3.17s6.355 1.96 7.801 3.17c.645.54.847 1.453.592 2.284l-.348 1.134c-.296.964-1.14 1.616-2.093 1.616H32.548c-.953 0-1.798-.652-2.093-1.616l-.348-1.134c-.255-.83-.053-1.744.592-2.285"
        className="fill-illustration-primary"
      />
      <path
        fillRule="evenodd"
        d="M109.223 28.277a.94.94 0 0 0-.667-.277h-6.612a.944.944 0 0 0-.944.944v6.612c0 .25.1.49.277.667l8.5 8.5a.944.944 0 0 0 1.335 0l6.611-6.61a.944.944 0 0 0 0-1.336zm-4.917 4.445a1.417 1.417 0 1 1-.001-2.833 1.417 1.417 0 0 1 .001 2.833"
        clipRule="evenodd"
        className="fill-illustration-primary"
      />
      <path
        fill="#b6babf"
        d="M59 30.5c0-1.38 1.02-2.5 2.28-2.5h16.44c1.259 0 2.28 1.12 2.28 2.5S78.979 33 77.72 33H61.28C60.02 33 59 31.88 59 30.5m0 9c0-1.38 1.04-2.5 2.323-2.5h25.354C87.96 37 89 38.12 89 39.5S87.96 42 86.677 42H61.323C60.04 42 59 40.88 59 39.5"
      />
      <path
        fill="#d2d5d9"
        d="M43.904 68.688c0 2.589-1.972 4.688-4.404 4.688s-4.404-2.099-4.404-4.688S37.068 64 39.5 64s4.404 2.099 4.404 4.688M31.699 78.966c1.446-1.211 4.323-3.17 7.801-3.17s6.355 1.959 7.801 3.17c.645.54.847 1.453.592 2.284l-.348 1.134c-.296.964-1.14 1.616-2.093 1.616H33.548c-.953 0-1.798-.652-2.093-1.616l-.348-1.134c-.255-.831-.053-1.744.592-2.284"
      />
      <path
        fill="#d2d5d9"
        fillRule="evenodd"
        d="M110.223 64.277a.94.94 0 0 0-.667-.277h-6.612a.944.944 0 0 0-.944.944v6.612c0 .25.1.49.277.667l8.5 8.5a.943.943 0 0 0 1.335 0l6.611-6.611a.943.943 0 0 0 0-1.335zm-4.917 4.445a1.416 1.416 0 1 1-.001-2.833 1.416 1.416 0 0 1 .001 2.833"
        clipRule="evenodd"
      />
      <path
        fill="#d2d5d9"
        d="M60 70.5c0-1.381 1.021-2.5 2.28-2.5h16.44c1.259 0 2.28 1.119 2.28 2.5S79.979 73 78.72 73H62.28C61.021 73 60 71.881 60 70.5m0 9c0-1.381 1.04-2.5 2.323-2.5h25.354C88.96 77 90 78.119 90 79.5S88.96 82 87.677 82H62.323C61.04 82 60 80.881 60 79.5"
      />
      <defs>
        <filter
          id={`${id}-a`}
          width="135"
          height="167"
          x="6"
          y="-1"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_2369_11352" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_2369_11352" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

function DiscountIllustration(props: IllustrationProps) {
  return (
    <svg width="155" height="172" fill="none" viewBox="0 0 155 172" aria-hidden="true" {...props}>
      <path
        fill="#f0f1f2"
        d="M81 162c40.869 0 74-33.131 74-74s-33.131-74-74-74S7 47.13 7 88s33.13 74 74 74"
      />
      <path fill="#828a91" d="M66 102 92.8 6.4c.8-2.8 2.7-5.1 5.2-6.4l-8 83.6L72 104z" />
      <path
        d="M75.8 106.7c.5-1.6-.1-3.3-1.5-4.4L69 98.8c-.7-.5-1.6-.8-2.4-.8-1.6 0-3.1 1-3.7 2.4L52.6 124c-1.7 3.9-4.6 7-8.3 9-1.3.7-2.8 1.2-4.2 1.6-8.6 2.3-14.8 10.6-13.9 20.1.8 8.7 7.7 15.9 16.4 17 8.2 1 15.6-3.2 19.1-9.7l4.1-6.4c1.4-2.2 1.3-4.9-.1-7.1-1.1-1.6-1.4-3.5-.8-5.4zM45 166c-7.2 0-13-5.8-13-13s5.8-13 13-13 13 5.8 13 13-5.8 13-13 13"
        className="fill-illustration-secondary"
      />
      <path fill="#e9af58" d="M110 14H0v62h110z" />
      <path fill="#d9942d" d="M102 22H8v46h94z" />
      <path
        fill="#fff"
        d="M91.5 31h-3v4h3zm0 8h-3v4h3zm0 8h-3v4h3zm0 8h-3v4h3zm0-33h-3v5h3zm0 41h-3v5h3zM44.4 36.4c.8-.8.8-2 0-2.8s-2-.8-2.8 0l-20 20c-.8.8-.8 2 0 2.8s2 .8 2.8 0zM25 42.5c-3 0-5.5-2.5-5.5-5.5s2.5-5.5 5.5-5.5 5.5 2.5 5.5 5.5-2.5 5.5-5.5 5.5m0-8c-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5-1.1-2.5-2.5-2.5m16 24c-3 0-5.5-2.5-5.5-5.5s2.5-5.5 5.5-5.5 5.5 2.5 5.5 5.5-2.5 5.5-5.5 5.5m0-8c-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5-1.1-2.5-2.5-2.5"
      />
      <path fill="#d2d5d9" d="M92 102 65.2 6.4C64.4 3.6 62.5 1.3 60 0l8 83.6L86 104z" />
      <path
        fill="#e8e9eb"
        d="m62.2 9.5 17.4 62.2c.6 2.2 2.6 3.8 4.9 3.8l-19.3-69C64.4 3.6 62.5 1.3 60 0l.6 6.1c.7 1 1.3 2.2 1.6 3.4"
      />
      <path
        fill="#828a91"
        d="M78 86c.796 0 1.559-.316 2.121-.879a2.996 2.996 0 0 0 0-4.242 2.996 2.996 0 0 0-4.242 0 2.996 2.996 0 0 0 0 4.242A3 3 0 0 0 78 86"
      />
      <path
        d="M118 135.7c-1.5-.4-2.9-.9-4.2-1.6-3.7-2-6.7-5.2-8.3-9l-10.3-23.6C94.4 100 93 99 91.4 99c-.9 0-1.7.3-2.4.8l-5.3 3.5c-1.3 1-1.9 2.7-1.5 4.4l11 36.4c.5 1.8.2 3.8-.8 5.4-1.4 2.1-1.5 4.9-.1 7.1l2.6 4.1c2.1-.4 4.2-.9 6.2-1.5-.7-1.6-1.1-3.4-1.1-5.2 0-7.2 5.8-13 13-13 5 0 9.3 2.8 11.5 6.9 1.6-1.2 3.2-2.4 4.7-3.8-2.5-4.1-6.5-7.1-11.2-8.4"
        className="fill-illustration-primary"
      />
    </svg>
  );
}

function OrdersIllustration(props: IllustrationProps) {
  const id = useSvgId();
  return (
    <svg width="265" height="260" fill="none" viewBox="0 0 265 260" aria-hidden="true" {...props}>
      <ellipse cx="128.5" cy="131.5" fill="#e5e5e5" fillOpacity=".5" rx="121.5" ry="123.5" />
      <g filter={`url(#${id}-a)`}>
        <path
          fill="#fefefe"
          d="M39.897 189.701a21 21 0 0 0-2.5-.384c-.349-2.67-.159-11.225-.159-14.309v-29.169l.01-97.956c.001-3.758-.287-30.679.49-32.034 1.122-.88 1.907-1.005 3.318-1.084 5.8-.321 11.65-.207 17.46-.197l27.537.019 91.107-.047c.003 13.302-.374 28.846.087 42.012l41.927.016-.009 87.404c0 15.146.157 30.584-.021 45.711 4.057.03 12.021.28 15.703-.142-29.099 54.104-90.516 78.199-148.537 56.865-25.014-9.198-52.359-32.078-63.237-56.828 5.093.273 11.668.133 16.824.123"
        />
      </g>
      <path
        d="M39.897 189.7c6.663.211 13.297-.137 19.95-.183l31.114-.02c1.192.289 1.884.2 2.51 1.379 3.923 7.382 5.14 17.24 8.698 24.669l12.063-.024c15.233.087 32.772.377 47.885-.031a862 862 0 0 0 4.674-14.049c1.218-3.721 2.476-7.885 4.169-11.384 3.126-.552 12.258-.328 16.038-.327l32.146-.047c4.057.029 12.021.28 15.703-.143-29.099 54.105-90.516 78.2-148.537 56.865-25.014-9.197-52.359-32.078-63.237-56.827 5.093.272 11.668.132 16.824.122"
        className="fill-illustration-primary"
      />
      <path
        d="M114.232 215.521c15.232.087 32.771.377 47.885-.031-.897 3.543-3.082 9.287-4.727 12.482-3.204.724-8.499.54-11.949.544l-15.123.005-13.41-.009c-3.083-.004-7.1.102-10.084-.368-1.811-2.967-3.881-9.072-4.655-12.599z"
        className="fill-illustration-secondary"
      />
      <path
        fill="#b8babd"
        d="M90.96 189.497c2.846.073 12.478-.088 14.298.732 1.517 2.015 8.331 22.174 8.974 25.292l-12.063.024c-3.558-7.429-4.775-17.287-8.698-24.669-.627-1.179-1.318-1.09-2.51-1.379"
      />
      <path
        fill="#ededec"
        d="M61.22 129.215c5.93.194 12.865.041 18.859.041l35.93-.015c-.311 18.132.177 36.614-.052 54.806l-36.301.028c-5 .01-13.61-.239-18.392.143z"
      />
      <path
        fill="#ec8471"
        d="M80.754 135.51c5.306.243 11.16-.109 16.205.199 1.458 8.264 2.78 17.446 4.343 25.536-.127 1.973-3.761 13.753-4.578 16.478-4.898-.234-11.199-.16-16.06-.012-1.129-3.457-4.348-13.296-4.74-16.406.864-7.224 3.4-18.309 4.83-25.795"
      />
      <path
        fill="#de695c"
        d="M75.925 161.305c2.27.32 8.08.234 10.675.21 4.592-.044 10.225.201 14.702-.27-.127 1.973-3.761 13.753-4.578 16.478-4.898-.234-11.199-.161-16.06-.013-1.129-3.456-4.348-13.295-4.74-16.405"
      />
      <path
        fill="#ededec"
        d="M61.234 56.454c17.85.349 36.875.09 54.776.032-.329 17.937-.028 36.822-.031 54.836l-54.71-.03z"
      />
      <path
        d="M67.673 75.97q20.239-.125 40.478-.039c-.437 2.225-.826 4.395-1.69 6.503-6.642 18.81-30.05 19.17-37.233.007-.99-2.418-1.293-3.87-1.555-6.471"
        className="fill-illustration-secondary"
      />
      <path
        d="M67.673 75.97q20.239-.125 40.478-.039c-.437 2.225-.826 4.395-1.69 6.503q-18.616-.157-37.233.007c-.99-2.418-1.293-3.87-1.555-6.471"
        className="fill-illustration-primary"
      />
      <path
        fill="#77aae2"
        d="M61.618 30.806c10.646-.83 22.552.28 33.313-.148 2.496-.1 5.212.063 7.351 1.44 1.3 3.468 1.294 6.43-2.4 8.052-4.46.43-37.974.542-39.72-.406-.849-.461-1.467-1.362-1.725-2.277-.402-1.42-.452-3.58.332-4.882.674-1.12 1.64-1.461 2.849-1.78"
      />
      <path
        fill="#ededec"
        d="M137.515 158.303c9.465-.387 19.998.141 29.797-.053 7.921.182 16.145-.378 24.008.3 1.853.16 3.195 4.392-.125 6.037-1.71.265-6.724.166-8.602.167l-16.676.006-18.559.012c-2.391.001-9.778.718-11.234-1.317-1.185-1.658-.392-4.033 1.391-5.152m2.197-85.509c2.193-.117 4.636-.146 6.829-.117 14.864.193 29.904-.325 44.751.125 2.664 1.839 2.893 5.057-.222 6.176-6.104.194-50.529.693-52.805-.297-.427-.186-.665-.59-.817-1.016-.397-1.115-.457-2.747.027-3.834.562-.742 1.32-.811 2.237-1.037m-1.128 71.071c6.525-.376 15.121-.019 21.783-.111 4.437-.061 14.127-.217 18.229.289q.171.108.338.224c2.335 1.649 2.053 5.494-1.372 5.719-4.713.165-39.144.674-40.886-.456-.56-.363-.884-.987-1.02-1.626-.186-.873-.268-2.048.282-2.81.635-.88 1.64-1.095 2.646-1.229m1.696-56.561c3.794-.21 9.357-.002 13.262-.053 3.53-.047 11.1-.187 14.217.353 1.906 2.316 2.093 5.174-1.447 5.883-3.962.163-25.785.917-28.003-.486-.593-.375-.996-1.26-1.137-1.929-.202-.96.068-2.005.614-2.807.487-.715 1.713-.826 2.494-.961"
      />
      <defs>
        <filter
          id={`${id}-a`}
          width="227.374"
          height="255.193"
          x="14.273"
          y="4.74"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dx="-1" dy="-2" />
          <feGaussianBlur stdDeviation="3.9" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_4205_10650" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_4205_10650" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

function PriceListIllustration(props: IllustrationProps) {
  const id = useSvgId();
  return (
    <svg width="164" height="168" fill="none" viewBox="0 0 164 168" aria-hidden="true" {...props}>
      <path fill="#f0f1f2" d="M0 74a74 74 0 1 0 148.002 0A74 74 0 0 0 0 74" />
      <mask
        id={`${id}-a`}
        width="74"
        height="148"
        x="0"
        y="0"
        maskUnits="userSpaceOnUse"
        style={{ maskType: 'luminance' }}
      >
        <path fill="#fff" d="M0 74a74 74 0 0 0 74 74V0A74 74 0 0 0 0 74" />
      </mask>
      <g mask={`url(#${id}-a)`}>
        <g filter={`url(#${id}-b)`}>
          <path
            fill="#fff"
            d="M62 21.4c0-1.7-1.4-3-3.2-3H-7.8c-1.8 0-3.2 1.3-3.2 3v106c0 1.7 1.4 3 3.2 3h66.6c1.8 0 3.2-1.3 3.2-3z"
          />
        </g>
        <path fill="#e8e9eb" d="M3 51h45v45H3z" />
        <path
          fill="#9fa5ab"
          fillRule="evenodd"
          d="M17.5 72.3a6.65 6.65 0 1 0 0-13.3 6.65 6.65 0 0 0 0 13.3m0 3a9.65 9.65 0 1 0 0-19.3 9.65 9.65 0 0 0 0 19.3"
          clipRule="evenodd"
        />
        <path fill="#b6babf" d="M42 56H17l-2 40h26l-4-30 5-8z" />
        <path fill="#9fa5ab" d="M16.9 56H22v40h-7l1.8-40z" />
        <path
          fill="#f0f1f2"
          d="M11.5 106c0-.796.445-1.559 1.238-2.121s1.868-.879 2.99-.879h22.545c1.12 0 2.196.316 2.989.879S42.5 105.204 42.5 106v12c0 .796-.445 1.559-1.238 2.121s-1.868.879-2.99.879H15.728c-1.12 0-2.196-.316-2.989-.879S11.5 118.796 11.5 118z"
        />
      </g>
      <path
        d="M48 34.4a2 2 0 1 0 0-4 2 2 0 0 0 0 4m0-8c3.3 0 6 2.9 6 6.4 0 6-5.4 9.3-5.6 9.5l-.4.1-.4-.1c-.2-.2-5.6-3.5-5.6-9.5 0-3.5 2.7-6.4 6-6.4"
        className="fill-illustration-secondary"
      />
      <g filter={`url(#${id}-c)`}>
        <path
          fill="#fff"
          d="M159 21.4a3 3 0 0 0-3-3H89a3 3 0 0 0-3 3v106a3 3 0 0 0 3 3h67a3 3 0 0 0 3-3z"
        />
      </g>
      <path
        fill="#b6babf"
        d="M130 34.4a3 3 0 0 0-3-3h-24a3 3 0 0 0-3 3 3 3 0 0 0 3 3h24a3 3 0 0 0 3-3"
      />
      <path
        fill="#787878"
        d="M18.23 115.818v-8.182h.524v8.182zm1.39-5.481a.81.81 0 0 0-.33-.601q-.29-.214-.789-.214-.34 0-.572.096a.8.8 0 0 0-.358.259.62.62 0 0 0-.121.377.54.54 0 0 0 .073.307.8.8 0 0 0 .227.227q.144.092.332.163.189.066.403.115l.588.14q.429.096.786.256.359.16.62.393.262.234.406.55.147.317.15.725a1.8 1.8 0 0 1-.306 1.042q-.3.438-.87.681-.566.24-1.364.24-.793 0-1.381-.243a2.03 2.03 0 0 1-.914-.719q-.325-.48-.342-1.186h1.34q.021.329.188.55.17.217.45.329.285.108.643.109.352 0 .61-.103a.94.94 0 0 0 .406-.284.66.66 0 0 0 .144-.419.55.55 0 0 0-.131-.371 1 1 0 0 0-.377-.255 4 4 0 0 0-.604-.192l-.713-.179q-.828-.201-1.307-.63-.48-.428-.477-1.153a1.7 1.7 0 0 1 .317-1.039q.323-.444.885-.694a3.1 3.1 0 0 1 1.279-.249q.728 0 1.272.249.546.25.85.694t.313 1.029zm5.156-1.882V115h-1.384v-5.232h-.039l-1.498.94v-1.228l1.62-1.025zm4.122 6.689q-.825-.003-1.42-.406-.59-.403-.91-1.167-.317-.763-.313-1.837 0-1.07.316-1.825.32-.755.91-1.148.596-.396 1.417-.396.82 0 1.412.396.595.397.914 1.151.32.751.317 1.822 0 1.077-.32 1.841-.316.764-.907 1.166-.593.403-1.416.403m0-1.148q.562 0 .898-.565.335-.566.332-1.697 0-.745-.153-1.24-.15-.496-.428-.745a.94.94 0 0 0-.65-.249q-.558 0-.894.559-.336.56-.339 1.675 0 .754.15 1.259.153.501.432.754a.95.95 0 0 0 .652.249m6.196 1.148q-.825-.003-1.419-.406-.592-.403-.91-1.167-.318-.763-.314-1.837 0-1.07.316-1.825.32-.755.911-1.148.595-.396 1.416-.396t1.413.396q.593.397.914 1.151.32.751.316 1.822 0 1.077-.32 1.841-.316.764-.907 1.166-.591.403-1.416.403m0-1.148q.562 0 .898-.565.336-.566.333-1.697 0-.745-.154-1.24-.15-.496-.428-.745a.94.94 0 0 0-.649-.249q-.56 0-.895.559-.336.56-.339 1.675 0 .754.15 1.259.154.501.432.754a.95.95 0 0 0 .652.249"
      />
      <path
        d="M145 34.4a1.998 1.998 0 0 0 1.414-3.414A1.998 1.998 0 0 0 143 32.4c0 1.1 1 2 2 2m0-8c3.4 0 6 2.9 6 6.4 0 6-5.3 9.3-5.6 9.5l-.3.1-.4-.1c-.2-.2-5.6-3.5-5.6-9.5 0-3.5 2.7-6.4 6-6.4z"
        className="fill-illustration-primary"
      />
      <path fill="#e8e9eb" d="M100 52h45v45h-45z" />
      <path
        fillRule="evenodd"
        d="M114.4 73.3a6.65 6.65 0 0 0 6.65-6.65 6.65 6.65 0 0 0-4.105-6.144 6.65 6.65 0 0 0-7.247 1.442A6.651 6.651 0 0 0 114.4 73.3m0 3a9.65 9.65 0 0 0 9.65-9.65A9.653 9.653 0 0 0 114.4 57a9.65 9.65 0 0 0-9.65 9.65 9.653 9.653 0 0 0 9.65 9.65"
        clipRule="evenodd"
        className="fill-illustration-secondary"
      />
      <path d="M139 57h-25.2L112 97h26l-4-30 5-8z" className="fill-illustration-secondary" />
      <path d="M113.8 57h5.2v40h-7z" className="fill-illustration-secondary" />
      <path d="M50 74a24 24 0 1 0 48 0 24 24 0 0 0-48 0" className="fill-illustration-primary" />
      <path
        fill="#f0f1f2"
        d="M108.5 106c0-.796.417-1.559 1.158-2.121.742-.563 1.748-.879 2.797-.879h21.09c1.049 0 2.055.316 2.797.879.741.562 1.158 1.325 1.158 2.121v12c0 .796-.417 1.559-1.158 2.121-.742.563-1.748.879-2.797.879h-21.09c-1.049 0-2.055-.316-2.797-.879-.741-.562-1.158-1.325-1.158-2.121z"
      />
      <path
        fill="#313131"
        d="M117.229 115.818v-8.182h.525v8.182zm1.391-5.481a.81.81 0 0 0-.33-.601q-.29-.214-.789-.214-.338 0-.572.096a.8.8 0 0 0-.358.259.62.62 0 0 0-.121.377.54.54 0 0 0 .073.307.8.8 0 0 0 .227.227 2 2 0 0 0 .332.163q.189.066.403.115l.588.14q.429.096.786.256.359.16.62.393.262.234.406.55.147.317.151.725a1.8 1.8 0 0 1-.307 1.042q-.3.438-.87.681-.566.24-1.364.24-.793 0-1.381-.243a2 2 0 0 1-.914-.719q-.326-.48-.342-1.186h1.339q.023.329.189.55.17.217.45.329.285.108.643.109.352 0 .61-.103a.94.94 0 0 0 .406-.284.66.66 0 0 0 .144-.419.55.55 0 0 0-.131-.371 1 1 0 0 0-.377-.255 4 4 0 0 0-.604-.192l-.713-.179q-.828-.201-1.307-.63-.48-.428-.476-1.153a1.7 1.7 0 0 1 .316-1.039q.323-.444.885-.694a3.1 3.1 0 0 1 1.279-.249q.727 0 1.272.249.546.25.85.694t.313 1.029zm4.699 4.752q-.678 0-1.208-.249a2.1 2.1 0 0 1-.838-.687 1.77 1.77 0 0 1-.323-1.003h1.343a.82.82 0 0 0 .319.616 1.1 1.1 0 0 0 .707.237q.326 0 .575-.144a1.03 1.03 0 0 0 .393-.406q.144-.262.144-.601 0-.345-.147-.607a1.03 1.03 0 0 0-.4-.409 1.2 1.2 0 0 0-.585-.15q-.287 0-.559.118a1 1 0 0 0-.419.323l-1.23-.221.31-3.451h4.001v1.131h-2.86l-.169 1.639h.038q.173-.242.521-.402a1.8 1.8 0 0 1 .78-.16q.59 0 1.054.278a2 2 0 0 1 .732.764q.269.483.266 1.112a2.2 2.2 0 0 1-.307 1.176q-.307.512-.86.806-.55.29-1.278.29m5.976.055q-.825-.003-1.419-.406-.591-.403-.911-1.167-.316-.763-.313-1.837 0-1.07.316-1.825.32-.755.911-1.148.596-.396 1.416-.396.822 0 1.413.396.594.397.914 1.151.32.751.316 1.822 0 1.077-.319 1.841-.317.764-.908 1.166-.591.403-1.416.403m0-1.148q.563 0 .898-.565.336-.566.333-1.697 0-.745-.154-1.24-.15-.496-.428-.745a.94.94 0 0 0-.649-.249q-.56 0-.895.559-.335.56-.338 1.675 0 .754.15 1.259.153.501.431.754a.95.95 0 0 0 .652.249"
      />
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M58.9 74c0 1.1.9 2 2 2h24.8a2 2 0 0 0 0-4H60.9a2 2 0 0 0-2 2"
        clipRule="evenodd"
      />
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M77.6 83.4c.8.8 2 .8 2.8 0l8-8c.8-.8.8-2 0-2.8l-8-8a1.998 1.998 0 0 0-3.014-.213A2 2 0 0 0 77.6 67.4l6.6 6.6-6.6 6.6a2 2 0 0 0 0 2.8"
        clipRule="evenodd"
      />
      <path fill="#b6babf" d="M9.7 37.4H30a3 3 0 0 0 0-6H13.5a74 74 0 0 0-3.8 6" />
      <defs>
        <filter
          id={`${id}-b`}
          width="83"
          height="122"
          x="-16"
          y="14.4"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2.5" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_788_8719" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_788_8719" result="shape" />
        </filter>
        <filter
          id={`${id}-c`}
          width="83"
          height="122"
          x="81"
          y="14.4"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2.5" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_788_8719" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_788_8719" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

function SalesAgentIllustration(props: IllustrationProps) {
  const id = useSvgId();
  return (
    <svg width="216" height="200" fill="none" viewBox="0 0 216 200" aria-hidden="true" {...props}>
      <circle cx="108" cy="100" r="100" fill="#f0f1f2" />
      <g clipPath={`url(#${id}-a)`}>
        <path
          fill="#fff"
          d="M167.517 57.879h-11.513c-.578 0-1.047.468-1.047 1.047v11.513c0 .578.469 1.047 1.047 1.047h11.513c.579 0 1.047-.469 1.047-1.047V58.926c0-.579-.468-1.047-1.047-1.047"
        />
        <path stroke="#fff" strokeLinecap="round" strokeWidth="4.187" d="M157.051 79.861h22.503" />
        <path
          strokeWidth="4.187"
          d="M52.383 147.107a4.187 4.187 0 1 0 .001 8.374 4.187 4.187 0 0 0 0-8.374Z"
          className="stroke-illustration-primary"
        />
        <path
          strokeWidth="4.187"
          d="M44.402 151.291H22.03c-10.983 0-19.886-8.903-19.886-19.887s8.903-19.886 19.886-19.886h171.41"
          className="stroke-illustration-primary"
        />
        <g filter={`url(#${id}-b)`}>
          <path
            fill="#fff"
            d="M175.815 28.574H92.081a2.093 2.093 0 0 0-2.093 2.094v50.24c0 1.156.937 2.093 2.093 2.093h83.734a2.094 2.094 0 0 0 2.094-2.093v-50.24a2.094 2.094 0 0 0-2.094-2.094"
          />
        </g>
        <path
          fill="#ffebc5"
          d="M89.413 28.572c-1.123 0-2.041.875-2.041 1.944v50.54c0 1.069.918 1.943 2.04 1.943h38.78V28.572z"
        />
        <path
          fill="#deac90"
          fillRule="evenodd"
          d="m99.508 70.65 6.634 2.681 5.98 3.53.813-13.492s7.323 1.883 8.448-.124c.934-1.665-.278-2.725.322-3.007.355-.167.949-1.327.213-1.906.539-.242.453-1.11.357-1.378-.141-.392-.508-.253-.253-.909.195-.5 1.105-.501 1.073-1.5-.031-.998-2.798-3.57-3.218-4.486-.265-.579-.434-3.28-.963-4.406-.923-1.96-2.511-3.361-4.153-4.169a8.2 8.2 0 0 0-2.639-.788c-.927-.108-1.938-.186-2.868-.131-2.264.134-4.415 1.196-6.162 2.627-1.487 1.22-2.775 2.76-3.396 4.598-.773 2.284-.428 4.901.79 6.973.834 1.42 5.235 2.999 5.653 4.6z"
          clipRule="evenodd"
        />
        <path
          fill="#edbf48"
          fillRule="evenodd"
          d="M92.89 82.996h25.148s-6.529-18.952-16.685-15.793z"
          clipRule="evenodd"
        />
        <path
          fill="#181c21"
          fillRule="evenodd"
          d="M95.307 64.29c3.144 1.943 6.542 4.314 15.857 4.314v-7.928c0-1.829-1.794-4.483-2.349-5.613-.32-.654-.56-1.445-.223-2.089.171-.323.465-.562.76-.781 1.644-1.222 3.534-2.15 4.933-3.649 1.389-1.489 2.193-3.505 2.238-5.538 2.491 1.519 3.024 5.184 3.03 5.2.633-.56 1.377-1.187 1.377-2.47 0-4.787-11.289-11.755-20.445-4.22-9.057 7.454-5.178 22.774-5.178 22.774"
          clipRule="evenodd"
        />
        <path
          stroke="#e8e9eb"
          strokeLinecap="round"
          strokeWidth="4.187"
          d="M139.182 40.549h28.259m-28.259 9.418h20.319m-20.319 21.517h15.699"
        />
        <g filter={`url(#${id}-c)`}>
          <path
            fill="#fff"
            d="M30.403 77.766a2.093 2.093 0 0 0-2.093 2.093v88.967c0 1.156.937 2.093 2.093 2.093h65.94a2.093 2.093 0 0 0 2.094-2.093V79.859a2.094 2.094 0 0 0-2.094-2.093z"
          />
        </g>
        <path
          fill="#e8e9eb"
          d="M86.924 88.234h-47.1a2.093 2.093 0 0 0-2.094 2.094v45.007c0 1.156.937 2.093 2.093 2.093h47.1a2.093 2.093 0 0 0 2.094-2.093V90.328a2.093 2.093 0 0 0-2.093-2.094"
        />
        <path
          d="M50.29 94.51h-4.187a2.093 2.093 0 0 0-2.093 2.093v4.187c0 1.156.937 2.093 2.093 2.093h4.187a2.093 2.093 0 0 0 2.093-2.093v-4.187a2.093 2.093 0 0 0-2.093-2.093m0 14.652h-4.187a2.093 2.093 0 0 0-2.093 2.093v4.187c0 1.156.937 2.094 2.093 2.094h4.187a2.094 2.094 0 0 0 2.093-2.094v-4.187a2.093 2.093 0 0 0-2.093-2.093m0 14.658h-4.187a2.094 2.094 0 0 0-2.093 2.094v4.186c0 1.157.937 2.094 2.093 2.094h4.187a2.093 2.093 0 0 0 2.093-2.094v-4.186a2.094 2.094 0 0 0-2.093-2.094"
          className="fill-illustration-secondary"
        />
        <path
          stroke="#fff"
          strokeLinecap="round"
          strokeWidth="4.187"
          d="M61.803 113.354h17.793m-17.793 14.648h17.793M61.803 98.7h17.793"
        />
        <path
          stroke="#e8e9eb"
          strokeLinecap="round"
          strokeWidth="4.187"
          d="M52.383 148.943h34.54m-15.7 8.368h15.7"
        />
        <path
          strokeWidth="4.187"
          d="M169.612 75.932a4.186 4.186 0 1 0 0-8.373 4.186 4.186 0 0 0 0 8.373Z"
          className="stroke-illustration-primary"
        />
        <path
          strokeWidth="4.187"
          d="M173.799 71.74h19.886c10.984 0 19.887 8.904 19.887 19.887s-8.903 19.887-19.887 19.887h-2.093M37.73 147.107a4.187 4.187 0 1 0 0 8.374 4.187 4.187 0 0 0 0-8.374Z"
          className="stroke-illustration-primary"
        />
        <path
          strokeWidth="4.187"
          d="M33.543 151.295H22.029c-10.983 0-19.886-8.904-19.886-19.887"
          className="stroke-illustration-primary"
        />
        <path
          fill="#181c21"
          d="M111.779 98.96c0-1.156.938-2.093 2.094-2.093h63.847c1.157 0 2.094.937 2.094 2.093v37.681a2.093 2.093 0 0 1-2.094 2.093h-63.847a2.094 2.094 0 0 1-2.094-2.093z"
        />
        <path fill="#52525b" d="M179.814 103.145H111.78v8.373h68.034z" />
        <path
          d="M129.95 119.371c0-1.156.937-2.094 2.093-2.094h63.848c1.156 0 2.093.938 2.093 2.094v37.68a2.093 2.093 0 0 1-2.093 2.094h-63.848a2.093 2.093 0 0 1-2.093-2.094z"
          className="fill-illustration-secondary"
        />
        <path
          stroke="#fff"
          strokeLinecap="round"
          strokeWidth="4.187"
          d="M179.814 151.295h10.376m-52.376-26.953h2.193"
        />
      </g>
      <defs>
        <filter
          id={`${id}-b`}
          width="98.388"
          height="64.894"
          x="84.754"
          y="24.387"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="1.047" />
          <feGaussianBlur stdDeviation="2.617" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_788_9046" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_788_9046" result="shape" />
        </filter>
        <filter
          id={`${id}-c`}
          width="91.061"
          height="114.088"
          x="17.843"
          y="69.392"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="2.093" />
          <feGaussianBlur stdDeviation="5.233" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_788_9046" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_788_9046" result="shape" />
        </filter>
        <clipPath id={`${id}-a`}>
          <path fill="#fff" d="M0 0h215.714v200H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

function SettingsApiIllustration(props: IllustrationProps) {
  return (
    <svg fill="none" viewBox="0 0 44 44" aria-hidden="true" {...props}>
      <circle cx="22" cy="22" r="22" fill="#f0f1f2" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M27.25 16.75 32.5 22l-5.25 5.25m-10.5 0L11.5 22l5.25-5.25m7.5-3-4.5 16.5"
        className="stroke-illustration-primary"
      />
    </svg>
  );
}

function ShippingMethodIllustration(props: IllustrationProps) {
  const id = useSvgId();
  return (
    <svg width="140" height="150" fill="none" viewBox="0 0 140 150" aria-hidden="true" {...props}>
      <path
        fill="#f0f1f2"
        d="M140 80c0 38.66-31.34 70-70 70S0 118.66 0 80s31.34-70 70-70 70 31.34 70 70"
      />
      <path stroke="#d2d5d9" strokeLinecap="round" strokeWidth="4" d="M8 66h10M4 78h14m-8 12h8" />
      <g fill="#fff" filter={`url(#${id}-a)`}>
        <rect width="66" height="54" x="24" y="46" rx="4" />
        <path d="M94 62h17.5a4 4 0 0 1 3.2 1.6L126.2 79a4 4 0 0 1 .8 2.4V96a4 4 0 0 1-4 4H94z" />
      </g>
      <path
        d="M43 57h28a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H43a2 2 0 0 1-2-2V59a2 2 0 0 1 2-2"
        className="fill-illustration-primary"
      />
      <path d="M52 57h10v12l-5-3-5 3z" className="fill-illustration-secondary" />
      <path fill="#e8e9eb" d="M47 81h10a2 2 0 0 1 0 4H47a2 2 0 0 1 0-4" />
      <path d="M99 68h11.5l8.2 11H99z" className="fill-illustration-secondary" />
      <circle cx="44" cy="102" r="10" fill="#313131" />
      <circle cx="44" cy="102" r="4" fill="#b6babf" />
      <circle cx="108" cy="102" r="10" fill="#313131" />
      <circle cx="108" cy="102" r="4" fill="#b6babf" />
      <defs>
        <filter
          id={`${id}-a`}
          width="114"
          height="64"
          x="19"
          y="42"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

function ThemesIllustration(props: IllustrationProps) {
  const id = useSvgId();
  return (
    <svg width="140" height="150" fill="none" viewBox="0 0 140 150" aria-hidden="true" {...props}>
      <path
        fill="#f0f1f2"
        d="M140 80c0 38.66-31.34 70-70 70S0 118.66 0 80s31.34-70 70-70 70 31.34 70 70"
      />
      <g clipPath={`url(#${id}-a)`}>
        <g filter={`url(#${id}-b)`}>
          <mask
            id={`${id}-c`}
            width="103"
            height="126"
            x="22"
            y="26"
            maskUnits="userSpaceOnUse"
            style={{ maskType: 'alpha' }}
          >
            <path
              fill="#fff"
              d="M125 152H22V30.345c0-2.39.763-4.345 2.861-4.345h96.324c1.012 0 1.982.458 2.698 1.273.715.814 1.117 1.92 1.117 3.072z"
            />
          </mask>
          <g mask={`url(#${id}-c)`}>
            <path fill="#fff" d="M0 0h140v80c0 38.66-31.34 70-70 70S0 118.66 0 80z" />
          </g>
        </g>
        <g filter={`url(#${id}-d)`}>
          <mask
            id={`${id}-e`}
            width="103"
            height="133"
            x="15"
            y="19"
            maskUnits="userSpaceOnUse"
            style={{ maskType: 'alpha' }}
          >
            <path
              fill="#fff"
              d="M118 152H15V23.586c0-2.522.763-4.586 2.861-4.586h96.324c1.012 0 1.982.483 2.698 1.343.715.86 1.117 2.027 1.117 3.243z"
            />
          </mask>
          <g mask={`url(#${id}-e)`}>
            <path fill="#fff" d="M0 0h140v80c0 38.66-31.34 70-70 70S0 118.66 0 80z" />
          </g>
        </g>
        <path
          fill="#e8e9eb"
          d="M100.132 79H32.868c-.495 0-.97-.21-1.32-.586A2.07 2.07 0 0 1 31 77c0-1.1.84-2 1.868-2h67.264c.495 0 .97.21 1.321.586.35.375.547.884.547 1.414s-.197 1.04-.547 1.414a1.8 1.8 0 0 1-1.321.586m0 10H32.868c-.495 0-.97-.21-1.32-.586A2.07 2.07 0 0 1 31 87c0-1.1.84-2 1.868-2h67.264c.495 0 .97.21 1.321.586.35.375.547.884.547 1.414s-.197 1.04-.547 1.414a1.8 1.8 0 0 1-1.321.586m0 10H32.868c-.495 0-.97-.21-1.32-.586A2.07 2.07 0 0 1 31 97c0-1.1.84-2 1.868-2h67.264c.495 0 .97.21 1.321.586.35.375.547.884.547 1.414s-.197 1.04-.547 1.414a1.8 1.8 0 0 1-1.321.586m0 10H32.868c-.495 0-.97-.211-1.32-.586A2.07 2.07 0 0 1 31 107c0-1.1.84-2 1.868-2h67.264c.495 0 .97.211 1.321.586.35.375.547.884.547 1.414s-.197 1.039-.547 1.414a1.8 1.8 0 0 1-1.321.586m-67.606 10h54.948c.404 0 .793-.211 1.079-.586S89 117.53 89 117s-.16-1.039-.447-1.414c-.286-.375-.674-.586-1.08-.586H32.527c-.84 0-1.526.9-1.526 2 0 .53.16 1.039.447 1.414.286.375.674.586 1.08.586"
        />
        <path
          d="M31 32.5a.5.5 0 0 1 .5-.5h30a.5.5 0 0 1 .5.5v30a.5.5 0 0 1-.5.5h-30a.5.5 0 0 1-.5-.5z"
          className="fill-illustration-primary"
        />
        <path d="M62 32.5V63H31.5z" className="fill-illustration-secondary" />
      </g>
      <defs>
        <filter
          id={`${id}-b`}
          width="113"
          height="134"
          x="17"
          y="22"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_2001_2209" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_2001_2209" result="shape" />
        </filter>
        <filter
          id={`${id}-d`}
          width="113"
          height="141"
          x="10"
          y="15"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_2001_2209" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_2001_2209" result="shape" />
        </filter>
        <clipPath id={`${id}-a`}>
          <path fill="#fff" d="M0 0h140v80c0 38.66-31.34 70-70 70S0 118.66 0 80z" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ErrorStateIllustration(props: IllustrationProps) {
  const id = useSvgId();
  return (
    <svg
      width="200"
      height="200"
      fill="none"
      viewBox="0 0 52.917 52.917"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <filter
          id={`${id}-a`}
          width="1.279"
          height="1.417"
          x="-.139"
          y="-.209"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation="1.345" />
        </filter>
      </defs>
      <path
        fill="#f6f6f6"
        d="M46.25 21.985c-.294 9.724-1.285 29.834-10.387 22.781-8.069-6.252-12.098 2.402-18.05-.077C9.825 41.362.914 21.969 9.655 19.184c5.243-1.671 8.753.808 15.96-7.316 5.51-6.211 20.869 2.378 20.635 10.118z"
      />
      <g filter={`url(#${id}-a)`} opacity=".086" transform="translate(-25.683 -27.879)scale(1.79)">
        <circle cx="22.107" cy="34.008" r="3.698" />
        <circle cx="29.086" cy="29.976" r="7.063" />
        <circle cx="36.094" cy="32.924" r="5.45" />
      </g>
      <g fill="#fff" transform="translate(-25.683 -27.879)scale(1.79)">
        <circle cx="22.107" cy="34.008" r="3.698" />
        <circle cx="29.086" cy="29.976" r="7.063" />
        <circle cx="36.094" cy="32.924" r="5.45" />
      </g>
      {/* The face */}
      <g
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="stroke-illustration-primary"
      >
        <path strokeWidth="1.058" d="M28.454 29.065a2.295 2.295 0 0 0-2.965 1.576" />
        <path
          strokeWidth="1.059"
          d="m21.248 22.712 2.523 2.523m.001-2.523-2.523 2.523m7.3-2.523 2.523 2.523m.001-2.523-2.523 2.523"
        />
      </g>
    </svg>
  );
}

const EMPTY_STATE_ILLUSTRATIONS: Record<
  ImageType,
  (props: IllustrationProps) => React.JSX.Element
> = {
  allDone: AllDoneIllustration,
  customerGroup: CustomerGroupIllustration,
  dataTables: DataTablesIllustration,
  discount: DiscountIllustration,
  errorState: ErrorStateIllustration,
  formViews: FormViewsIllustration,
  forms: FormsIllustration,
  internalFields: InternalFieldsIllustration,
  orders: OrdersIllustration,
  priceList: PriceListIllustration,
  productSearch: ProductSearchIllustration,
  salesAgent: SalesAgentIllustration,
  settingsAPI: SettingsApiIllustration,
  shippingMethod: ShippingMethodIllustration,
  themes: ThemesIllustration,
  unsupported: UnsupportedIllustration,
  workflows: WorkflowsIllustration,
};

export function EmptyStateIllustration({
  imageType,
  ...props
}: IllustrationProps & { imageType: ImageType }) {
  const Illustration = EMPTY_STATE_ILLUSTRATIONS[imageType];
  return <Illustration {...props} />;
}
